'use strict';

import { USE_PIPELINE_STATUS_API_MOCKS } from '@/config/env.config';
import {
	getMockPipelineStatusSnapshotForScenario,
	requirePipelineStatusMockScenarioFromEnv,
} from '@/features/pipelineStatus/mocks/pipelineStatusFacts.mock';
import { MOCK_PIPELINE_STATUS_GRAPH_CATALOG } from '@/features/pipelineStatus/mocks/pipelineStatusGraphCatalog.mock';

/** @param {unknown} args @returns {string} */
function requestUrl(args) {
	if (typeof args === 'string') return args;
	if (args && typeof args === 'object' && typeof args.url === 'string') return args.url;
	return '';
}

/** @param {string} url */
function normalizePath(url) {
	let path = requestUrl(url);
	const qi = path.indexOf('?');
	const query = qi >= 0 ? path.slice(qi + 1) : '';
	if (qi >= 0) path = path.slice(0, qi);
	if (path.startsWith('/')) path = path.slice(1);
	path = path.replace(/^pipeline-tracker\//, 'pipeline-status/');
	path = path.replace(/^admin\/pipeline-status\//, 'pipeline-status/');
	return { path, query };
}

/** @param {string} query @param {string} key @param {number} defaultVal */
function intQueryParam(query, key, defaultVal) {
	const sp = new URLSearchParams(query);
	const raw = sp.get(key);
	if (raw == null || raw === '') return defaultVal;
	const n = parseInt(raw, 10);
	return Number.isFinite(n) ? n : defaultVal;
}

/**
 * @param {Record<string, unknown>[]} rows
 * @param {string | undefined} projectId
 */
function withProjectSubjectIds(rows, projectId) {
	if (!projectId) return rows;
	return rows.map((r) => {
		if (!r || typeof r !== 'object') return r;
		const row = /** @type {Record<string, unknown>} */ (r);
		if (String(row.scope ?? '') === 'Project') {
			return { ...row, subjectId: projectId };
		}
		return { ...row };
	});
}

/** @param {string} occurredAt */
function occurredMs(occurredAt) {
	const t = new Date(String(occurredAt || '')).getTime();
	return Number.isNaN(t) ? 0 : t;
}

/**
 * Fixture facts use frozen calendar dates. Shift them so the newest sits at `now`
 * (relative spacing preserved) before applying `minutes`, otherwise the activity
 * strip's last-hour window drops every mock row.
 * @param {Record<string, unknown>[]} rows
 * @param {number} [nowMs]
 */
function rebaseRowsToNow(rows, nowMs = Date.now()) {
	if (!Array.isArray(rows) || rows.length === 0) return rows;
	let latest = 0;
	for (const r of rows) {
		const t = occurredMs(r.occurredAt);
		if (t > latest) latest = t;
	}
	if (latest <= 0) return rows;
	const delta = nowMs - latest;
	return rows.map((r) => {
		const t = occurredMs(r.occurredAt);
		if (t <= 0) return r;
		return { ...r, occurredAt: new Date(t + delta).toISOString() };
	});
}

/** @param {unknown} value @returns {string | undefined} */
function nonemptyPipeline(value) {
	if (value == null) return undefined;
	const s = String(value).trim();
	return s === '' ? undefined : s;
}

/**
 * Project pipeline hint on the facts request. RTK `query()` returns `{ url, method, params }`
 * before fetchBaseQuery serializes `params` onto the URL, so read both.
 * @param {unknown} args
 * @param {string} query already-parsed URL query string (may be empty)
 * @returns {string | undefined}
 */
function readPipelineHintFromRequest(args, query) {
	if (args && typeof args === 'object') {
		const params = /** @type {Record<string, unknown>} */ (args).params;
		const fromParams = nonemptyPipeline(params && typeof params === 'object' ? params.pipeline : undefined);
		if (fromParams) return fromParams;
	}
	return nonemptyPipeline(new URLSearchParams(query).get('pipeline'));
}

/**
 * Fallback only: the project page loads via classic Redux (`projectsFetchDataById`), not
 * RTK `projectId`, so this cache is usually empty on `/home/project/…`. Prefer the
 * `pipeline` query param from {@link readPipelineHintFromRequest}.
 * @param {import('@reduxjs/toolkit/query').BaseQueryApi | undefined} api
 * @param {string | undefined} projectId
 * @returns {unknown}
 */
function tryReadProjectPipelineFromPortalCache(api, projectId) {
	if (!api || typeof api.getState !== 'function' || !projectId) return undefined;
	try {
		// Lazy require avoids static circular dependency: portal/index.api → baseQueryWithAuth → this module → portal/index.api
		const { portalApi } = require('@/redux/services/portal/index.api');
		const slice = portalApi.endpoints.projectId.select(projectId)(api.getState());
		const data = slice?.data;
		if (!data || typeof data !== 'object') return undefined;
		return nonemptyPipeline(/** @type {Record<string, unknown>} */ (data).pipeline);
	} catch {
		return undefined;
	}
}

/**
 * When {@link USE_PIPELINE_STATUS_API_MOCKS} is true, returns a fetchBaseQuery-shaped `{ data }`
 * for Portal RTK requests under `admin/pipeline-status/*` (legacy mock paths without `admin/` still normalize), so no HTTP is sent.
 * @param {unknown} args RTK `query` return value (or string url). Facts requests may include `params.pipeline`.
 * @param {import('@reduxjs/toolkit/query').BaseQueryApi | undefined} api RTK baseQuery api (cache fallback when the request has no pipeline hint)
 * @returns {Promise<{ data: unknown } | null>}
 */
export async function maybePortalPipelineStatusMockResponse(args, api) {
	if (!USE_PIPELINE_STATUS_API_MOCKS) return null;
	const method = (args && typeof args === 'object' && args.method) || 'GET';
	if (String(method).toUpperCase() !== 'GET') return null;

	const { path, query } = normalizePath(args);
	if (!path.startsWith('pipeline-status/')) return null;

	const factsMatch = /^pipeline-status\/projects\/([^/]+)\/facts$/.exec(path);
	const summaryMatch = !factsMatch && /^pipeline-status\/projects\/([^/]+)$/.exec(path);
	const projectIdFromPath = factsMatch?.[1] ?? summaryMatch?.[1];
	const projectPipeline =
		readPipelineHintFromRequest(args, query)
		?? tryReadProjectPipelineFromPortalCache(api, projectIdFromPath);
	const snapshot = getMockPipelineStatusSnapshotForScenario(
		requirePipelineStatusMockScenarioFromEnv(projectPipeline),
	);
	const baseRows = snapshot.facts;
	const baseManifests = snapshot.iterationManifests;

	if (path === 'pipeline-status/graphs') {
		return { data: MOCK_PIPELINE_STATUS_GRAPH_CATALOG };
	}

	// GET pipeline-status/activity?…
	if (path === 'pipeline-status/activity') {
		const takeRaw = new URLSearchParams(query).get('take');
		const take = takeRaw != null && takeRaw !== '' ? parseInt(takeRaw, 10) : undefined;
		const minutes = intQueryParam(query, 'minutes', 0);
		const nowMs = Date.now();
		const shifted = rebaseRowsToNow(baseRows, nowMs);
		const cutoff = minutes > 0 ? nowMs - minutes * 60 * 1000 : 0;
		const recent = minutes > 0
			? shifted.filter((r) => occurredMs(r.occurredAt) >= cutoff)
			: shifted;
		const sorted = [...recent].sort((a, b) => occurredMs(b.occurredAt) - occurredMs(a.occurredAt));
		const limited = Number.isFinite(take) ? sorted.slice(0, Math.max(0, take)) : sorted;
		return { data: limited };
	}

	// GET pipeline-status/records?…
	if (path === 'pipeline-status/records') {
		const skip = intQueryParam(query, 'skip', 0);
		const pageSize = intQueryParam(query, 'page_size', 100);
		const slice = baseRows.slice(skip, skip + pageSize);
		return {
			data: {
				items: slice,
				totalCount: baseRows.length,
				skip,
				take: pageSize,
			},
		};
	}

	// GET pipeline-status/projects/{projectId}/facts
	if (factsMatch) {
		const projectId = factsMatch[1];
		return {
			data: {
				facts: withProjectSubjectIds(baseRows, projectId),
				iterationManifests: withProjectSubjectIds(baseManifests, projectId),
				currentFlows: snapshot.currentFlows ?? null,
				graph: snapshot.graph,
			},
		};
	}

	// GET pipeline-status/projects/{projectId} — summary DTO list (minimal mock)
	if (summaryMatch) {
		return { data: [] };
	}

	return null;
}
